import * as React from 'react'
import { Alert, Button, Form } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { BankAccount, BusinessForm, ContractStatus } from 'api/common/constants'
import { connectorGetAPI } from 'api/connector'
import { bankBranchCodeLibrary, bankCodeLibrary } from 'api/external/bankCodeLibrary'
import postalCodeLibrary from 'api/external/postalCodeLibrary'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputField from 'components/InputField'
import InputRadio2 from 'components/InputRadio2'
import InputSelectField from 'components/InputSelectField'
import { scrollToAnchor } from 'utilities/helper'
import loginData from 'utilities/loginData'
import { connectSubscriptionFormItems } from 'utilities/resources/formItems'
import { ConnectSubscriptionFormValues } from 'utilities/resources/formItemValues'
import { getBankAccountTypeLabel, getBusinessFormLabel, prefectures } from 'utilities/resources/options'
import { birthdayFormatter, PropertySetter } from 'utilities/typeUtility'
import {
  ErrorChecker,
  FormErrorMessage,
  ValidateResult,
  bankAccountNameValidator,
  bankAccountNumberValidator,
  bankBranchCodeValidator,
  bankCodeValidator,
  postalCodeValidator,
  telValidator,
  emptyValidator,
} from 'utilities/validators'

type StateValue = {
  formValues?: ConnectSubscriptionFormValues
}

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const stateValue = React.useMemo(() => (location.state ?? {}) as StateValue, [location])
  const { formValues: prevFormValues } = stateValue

  // お申込み情報
  const [formValues, setFormValues] = React.useState<ConnectSubscriptionFormValues>({
    countryNumber: prevFormValues?.countryNumber ?? '+81',
    phoneNumber: prevFormValues?.phoneNumber ?? '',
    businessForm: prevFormValues?.businessForm ?? `${BusinessForm.Individual}`,
    companyName: prevFormValues?.companyName ?? '',
    companyNameKana: prevFormValues?.companyNameKana ?? '',
    companyInvoiceNumber: prevFormValues?.companyInvoiceNumber ?? '',
    lastName: prevFormValues?.lastName ?? '',
    firstName: prevFormValues?.firstName ?? '',
    lastNameKana: prevFormValues?.lastNameKana ?? '',
    firstNameKana: prevFormValues?.firstNameKana ?? '',
    lastNameAlphabet: prevFormValues?.lastNameAlphabet ?? '',
    firstNameAlphabet: prevFormValues?.firstNameAlphabet ?? '',
    gender: prevFormValues?.gender ?? '',
    email: prevFormValues?.email ?? '',
    birthday: prevFormValues?.birthday ?? '',
    bankCode: prevFormValues?.bankCode ?? '',
    bankName: prevFormValues?.bankName ?? '',
    bankType: prevFormValues?.bankType ?? BankAccount.Normal,
    bankBranchCode: prevFormValues?.bankBranchCode ?? '',
    bankBranchName: prevFormValues?.bankBranchName ?? '',
    bankAccountNumber: prevFormValues?.bankAccountNumber ?? '',
    bankAccountName: loginData.getUserType() === BusinessForm.Company ? prevFormValues?.bankAccountName : '',
    bankAccountLastName: loginData.getUserType() === BusinessForm.Individual ? prevFormValues?.bankAccountLastName : '',
    bankAccountFirstName: loginData.getUserType() === BusinessForm.Individual ? prevFormValues?.bankAccountFirstName : '',
    postalCode: prevFormValues?.postalCode ?? '',
    prefecture: prevFormValues?.prefecture ?? '',
    address: prevFormValues?.address ?? '',
    receivePostalCode: prevFormValues?.receivePostalCode ?? '',
    receivePrefecture: prevFormValues?.receivePrefecture ?? '',
    receiveAddress: prevFormValues?.receiveAddress ?? '',
  })
  const setter = new PropertySetter(setFormValues)

  // エラー制御
  const [requireEntered, setRequireEntered] = React.useState(false)
  const [committed, setCommitted] = React.useState(false)
  const [errorValues, setErrorValues] = React.useState<FormErrorMessage<ConnectSubscriptionFormValues>>()
  const errorChecker = React.useMemo(() => new ErrorChecker<ConnectSubscriptionFormValues>(connectSubscriptionFormItems), [])

  /**
   * 「登録する」ボタン
   */
  const handleRegisterButton = () => {
    errorChecker.init()
    errorChecker.check(telValidator(formValues.phoneNumber), 'phoneNumber')
    errorChecker.check(bankCodeValidator(formValues.bankCode), 'bankCode')
    errorChecker.check(bankBranchCodeValidator(formValues.bankBranchCode), 'bankBranchCode')
    errorChecker.check(bankAccountNumberValidator(formValues.bankAccountNumber), 'bankAccountNumber')
    // 法人の場合
    if (formValues.businessForm === `${BusinessForm.Company}`) {
      errorChecker.check(emptyValidator(formValues.bankAccountName), 'bankAccountName')
      if (formValues.bankAccountName) {
        errorChecker.check(bankAccountNameValidator(formValues.bankAccountName, formValues.companyNameKana), 'bankAccountName', {
          [ValidateResult.Mismatch]: 'お申し込みの法人名と同一名義の口座以外はご登録できません。',
        })
      }
    }
    // 個人の場合
    if (formValues.businessForm === `${BusinessForm.Individual}`) {
      errorChecker.check(emptyValidator(formValues.bankAccountLastName), 'bankAccountLastName')
      errorChecker.check(emptyValidator(formValues.bankAccountFirstName), 'bankAccountFirstName')
      if (formValues.bankAccountLastName && formValues.bankAccountFirstName) {
        errorChecker.check(bankAccountNameValidator(formValues.bankAccountLastName, formValues.lastNameKana), 'bankAccountLastName', {
          [ValidateResult.Mismatch]: 'お申し込みの姓（カナ）と同一名義の口座以外はご登録できません。',
        })
        errorChecker.check(bankAccountNameValidator(formValues.bankAccountFirstName, formValues.firstNameKana), 'bankAccountFirstName', {
          [ValidateResult.Mismatch]: 'お申し込みの名（カナ）と同一名義の口座以外はご登録できません。',
        })
      }
    }
    errorChecker.check(postalCodeValidator(formValues.postalCode), 'postalCode')
    errorChecker.check(postalCodeValidator(formValues.receivePostalCode), 'receivePostalCode')
    setErrorValues(errorChecker.getErrorMessageData())
    if (errorChecker.isValid()) {
      setCommitted(true)
      navigate('/connectorEditConfirm', { state: { ...stateValue, formValues } })
    } else {
      scrollToAnchor(errorChecker.getFirstInvalidItemId())
    }
  }

  // 住所ラベル
  const [addressLabel, setAddressLabel] = React.useState('')

  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
    } else {
      setRequireEntered(errorChecker.isAllEntered(formValues, ['must']))
      setAddressLabel(formValues.businessForm === `${BusinessForm.Individual}` ? 'ご登録住所' : '本店所在地')
    }
  }, [navigate, errorChecker, formValues])

  /**
   * 画面遷移時の処理
   */
  React.useEffect(() => {
    if (!prevFormValues) {
      connectorGetAPI((status, response) => {
        switch (loginData.getUserType()) {
          // 法人の場合
          case BusinessForm.Company:
            setFormValues({
              ...response.formValues,
              bankAccountName: response.formValues.bankAccountName,
            })
            break
          // 個人の場合　半角スペースで姓と名に分割
          case BusinessForm.Individual:
            const [bankAccountLastName, bankAccountFirstName] = response.formValues.bankAccountName?.split(' ') ?? []
            setFormValues({
              ...response.formValues,
              bankAccountName: '',
              bankAccountLastName: bankAccountLastName,
              bankAccountFirstName: bankAccountFirstName,
            })
            break
          default:
            break
        }
      })
    }
  }, [prevFormValues])
  return (
    <>
      <Header title="コネクター情報変更" />
      <main>
        <Form>
          <section className="mx-4 my-5">
            {loginData.getContractStatus() === ContractStatus.CorporateDocumentRejected && <Alert variant="danger">法人登録が非承認となりました。</Alert>}
            <div className="mt-5">
              <h3 className="fw-bold">お申込み情報</h3>
              <div className="mt-4">
                <div>{connectSubscriptionFormItems.email.label}</div>
                <div className="mt-1">{formValues.email ?? ''}</div>
              </div>
              <div className="mt-4">
                <div>{connectSubscriptionFormItems.businessForm.label}</div>
                <div className="mt-1">{getBusinessFormLabel(formValues.businessForm)}</div>
              </div>
              <div className="mt-4">
                <div>契約者氏名</div>
                <div className="mt-1">{`${formValues.lastName ?? ''} ${formValues.firstName ?? ''}`}</div>
              </div>
              <div className="mt-3">
                <div>契約者氏名（カナ）</div>
                <div>{`${formValues.lastNameKana ?? ''} ${formValues.firstNameKana ?? ''}`}</div>
              </div>
              {formValues.lastNameAlphabet && formValues.firstNameAlphabet && (
                <div className="mt-3">
                  <div>契約者氏名（ローマ字）</div>
                  <div>{`${formValues.lastNameAlphabet ?? ''} ${formValues.firstNameAlphabet ?? ''}`}</div>
                </div>
              )}
              {loginData.getUserType() === BusinessForm.Company && (
                <>
                  <div className="mt-3">
                    <div>法人名</div>
                    <div>{formValues.companyName ?? ''}</div>
                  </div>
                  <div className="mt-3">
                    <div>法人名（カナ）</div>
                    <div>{formValues.companyNameKana ?? ''}</div>
                  </div>
                </>
              )}
              <div className="mt-4">
                <div>{connectSubscriptionFormItems.birthday.label}</div>
                <div className="mt-1">{birthdayFormatter(formValues.birthday)}</div>
              </div>
              <InputField
                name={connectSubscriptionFormItems.companyName.name}
                value={formValues.countryNumber ?? ''}
                labelTop={connectSubscriptionFormItems.countryNumber.label}
                type="tel"
                bold
                className="mt-3"
                onChange={(e) => setter.set('countryNumber', e)}
                errors={[errorValues?.countryNumber ?? '']}
              />
              <InputField
                name={connectSubscriptionFormItems.phoneNumber.name}
                value={formValues.phoneNumber ?? ''}
                labelTop={connectSubscriptionFormItems.phoneNumber.label}
                type="tel"
                maxLength={11}
                pattern="[0-9]+"
                placeholder="08012345678"
                bold
                className="mt-3"
                onChange={(e) => setter.set('phoneNumber', e)}
                errors={[errorValues?.phoneNumber ?? '']}
              />
            </div>
            <div className="mt-5">
              <h3 className="fw-bold">報酬受取口座</h3>
              <InputField
                name={connectSubscriptionFormItems.bankCode.name}
                value={formValues.bankCode ?? ''}
                labelTop={connectSubscriptionFormItems.bankCode.label}
                type="text"
                maxLength={4}
                pattern="[0-9]{4}"
                placeholder="入力してください"
                className="mt-3"
                bold
                onChange={(e) => setter.set('bankCode', e)}
                onBlur={() => bankCodeLibrary(formValues.bankCode, (bankName) => setFormValues((values) => ({ ...values, bankName })))}
                notes={['※ゆうちょ銀行はご指定いただけません']}
                errors={[errorValues?.bankCode ?? '']}
              />
              <InputField
                name={connectSubscriptionFormItems.bankName.name}
                value={formValues.bankName ?? ''}
                labelTop={connectSubscriptionFormItems.bankName.label}
                type="text"
                placeholder="入力してください"
                className="mt-3"
                bold
                onChange={(e) => setter.set('bankName', e)}
                errors={[errorValues?.bankName ?? '']}
              />
              <div className="mt-3">
                <div className="fw-bold font-15">{connectSubscriptionFormItems.bankType.label}</div>
                <InputRadio2
                  name={connectSubscriptionFormItems.bankType.name}
                  label1={getBankAccountTypeLabel(BankAccount.Normal)}
                  label2={getBankAccountTypeLabel(BankAccount.Current)}
                  value1={BankAccount.Normal}
                  value2={BankAccount.Current}
                  checked={formValues.bankType ?? ''}
                  onChange={(e) => setter.set('bankType', e)}
                />
              </div>
              <InputField
                name={connectSubscriptionFormItems.bankBranchCode.name}
                value={formValues.bankBranchCode ?? ''}
                labelTop={connectSubscriptionFormItems.bankBranchCode.label}
                type="text"
                maxLength={3}
                pattern="[0-9]{3}"
                placeholder="入力してください"
                className="mt-3"
                bold
                onChange={(e) => setter.set('bankBranchCode', e)}
                onBlur={() => bankBranchCodeLibrary(formValues.bankCode, formValues.bankBranchCode, (bankBranchName) => setFormValues((values) => ({ ...values, bankBranchName })))}
                errors={[errorValues?.bankBranchCode ?? '']}
              />
              <InputField
                name={connectSubscriptionFormItems.bankBranchName.name}
                value={formValues.bankBranchName ?? ''}
                labelTop={connectSubscriptionFormItems.bankBranchName.label}
                type="text"
                placeholder="入力してください"
                className="mt-3"
                bold
                onChange={(e) => setter.set('bankBranchName', e)}
                errors={[errorValues?.bankBranchName ?? '']}
              />
              <InputField
                name={connectSubscriptionFormItems.bankAccountNumber.name}
                value={formValues.bankAccountNumber ?? ''}
                labelTop={connectSubscriptionFormItems.bankAccountNumber.label}
                type="text"
                maxLength={7}
                pattern="[0-9]{7}"
                placeholder="入力してください"
                className="mt-3"
                bold
                onChange={(e) => setter.set('bankAccountNumber', e)}
                errors={[errorValues?.bankAccountNumber ?? '']}
              />
              {/* 法人の場合　フリーの入力欄 */}
              {loginData.getUserType() === BusinessForm.Company && (
                <InputField
                  name={connectSubscriptionFormItems.bankAccountName?.name ?? ''}
                  value={formValues.bankAccountName ?? ''}
                  labelTop={connectSubscriptionFormItems.bankAccountName?.label ?? ''}
                  type="text"
                  maxLength={120}
                  placeholder="入力してください"
                  className="mt-3"
                  bold
                  onChange={(e) => setter.set('bankAccountName', e)}
                  errors={[errorValues?.bankAccountName ?? '']}
                />
              )}
              {/* 個人の場合　姓と名に入力欄をわける */}
              {loginData.getUserType() === BusinessForm.Individual && (
                <div className="d-flex gap-3 align-items-end">
                  <InputField
                    name={connectSubscriptionFormItems.bankAccountLastName?.name ?? ''}
                    value={formValues.bankAccountLastName ?? ''}
                    labelTop={connectSubscriptionFormItems.bankAccountLastName?.label}
                    type="text"
                    maxLength={120}
                    placeholder="姓（半角カナ）"
                    className="mt-3"
                    bold
                    onChange={(e) => setter.set('bankAccountLastName', e)}
                    errors={[errorValues?.bankAccountLastName ?? '']}
                  />
                  <InputField
                    name={connectSubscriptionFormItems.bankAccountFirstName?.name ?? ''}
                    value={formValues.bankAccountFirstName ?? ''}
                    labelTop=""
                    type="text"
                    maxLength={120}
                    placeholder="名（半角カナ）"
                    className="mt-3"
                    bold
                    onChange={(e) => setter.set('bankAccountFirstName', e)}
                    errors={[errorValues?.bankAccountFirstName ?? '']}
                  />
                </div>
              )}
            </div>
            <div className="mt-5">
              <h3 className="fw-bold">{addressLabel}</h3>
              <div className="mt-3 d-flex gap-3 align-items-end">
                <InputField
                  name={connectSubscriptionFormItems.postalCode.name}
                  value={formValues.postalCode ?? ''}
                  labelTop={connectSubscriptionFormItems.postalCode.label}
                  type="text"
                  maxLength={7}
                  pattern="[0-9]{7}"
                  placeholder="0000000"
                  className="flex-fill"
                  bold
                  onChange={(e) => setter.set('postalCode', e)}
                  errors={[errorValues?.postalCode ?? '']}
                />
                <div>
                  <Button
                    variant="primary"
                    className="p-3 text-nowrap"
                    onClick={() => postalCodeLibrary(formValues.postalCode, ({ prefcode, address }) => setFormValues((values) => ({ ...values, prefecture: prefcode, address })))}
                  >
                    住所検索
                  </Button>
                </div>
              </div>
              <div className="mt-4">
                <div className="fw-bold">住所</div>
                <InputSelectField
                  name={connectSubscriptionFormItems.prefecture.name}
                  value={formValues.prefecture ?? ''}
                  label={connectSubscriptionFormItems.prefecture.label}
                  items={prefectures}
                  className="mt-2"
                  onChange={(e) => setter.set('prefecture', e)}
                  errors={[errorValues?.prefecture ?? '']}
                />
                <InputField
                  name={connectSubscriptionFormItems.address.name}
                  value={formValues.address ?? ''}
                  labelTop={connectSubscriptionFormItems.address.label}
                  type="text"
                  maxLength={255}
                  placeholder="住所情報を入力してください"
                  className="mt-3"
                  onChange={(e) => setter.set('address', e)}
                  errors={[errorValues?.address ?? '']}
                />
              </div>
            </div>
            <div className="mt-5">
              <h3 className="fw-bold">郵便物受け取り住所</h3>
              <div>※{addressLabel}と同じ場合は入力不要です</div>
              <div className="mt-3 d-flex gap-3 align-items-end">
                <InputField
                  name={connectSubscriptionFormItems.receivePostalCode.name}
                  value={formValues.receivePostalCode ?? ''}
                  labelTop={connectSubscriptionFormItems.receivePostalCode.label}
                  type="text"
                  maxLength={7}
                  pattern="[0-9]{7}"
                  placeholder="0000000"
                  className="flex-fill"
                  bold
                  onChange={(e) => setter.set('receivePostalCode', e)}
                  errors={[errorValues?.receivePostalCode ?? '']}
                />
                <div>
                  <Button
                    variant="primary"
                    className="p-3 text-nowrap"
                    onClick={() =>
                      postalCodeLibrary(formValues.receivePostalCode, ({ prefcode, address }) =>
                        setFormValues((values) => ({ ...values, receivePrefecture: prefcode, receiveAddress: address })),
                      )
                    }
                  >
                    住所検索
                  </Button>
                </div>
              </div>
              <div className="mt-4">
                <div className="fw-bold">住所</div>
                <InputSelectField
                  name={connectSubscriptionFormItems.receivePrefecture.name}
                  value={formValues.receivePrefecture ?? ''}
                  label={connectSubscriptionFormItems.receivePrefecture.label}
                  items={prefectures}
                  className="mt-2"
                  onChange={(e) => setter.set('receivePrefecture', e)}
                  errors={[errorValues?.receivePrefecture ?? '']}
                />
                <InputField
                  name={connectSubscriptionFormItems.receiveAddress.name}
                  value={formValues.receiveAddress ?? ''}
                  labelTop={connectSubscriptionFormItems.receiveAddress.label}
                  type="text"
                  maxLength={255}
                  placeholder="住所情報を入力してください"
                  className="mt-3"
                  onChange={(e) => setter.set('receiveAddress', e)}
                  errors={[errorValues?.receiveAddress ?? '']}
                />
              </div>
            </div>
          </section>
          <section className="mx-4 my-5">
            <div>
              <Button variant="primary" className="w-100 py-4" onClick={handleRegisterButton} disabled={!(requireEntered && !committed)}>
                入力内容を確認する
              </Button>
            </div>
            {loginData.isRegistrationCompleted() && (
              <div className="mt-5 text-center">
                <Button variant="link" href="/myPage">
                  マイページへ戻る
                </Button>
              </div>
            )}
          </section>
        </Form>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
